import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check, CreditCard, Building2 } from 'lucide-react';

export default function AdminPaymentDetails() {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    account_number: '',
    bank_name: '',
    ifsc_code: '',
    branch: '',
    upi_id: ''
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const fetchBankDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin-bank-details`, {
        headers: getAuthHeaders()
      });
      if (response.data && response.data.account_holder_name) {
        setFormData({
          account_holder_name: response.data.account_holder_name || '',
          account_number: response.data.account_number || '',
          bank_name: response.data.bank_name || '',
          ifsc_code: response.data.ifsc_code || '',
          branch: response.data.branch || '',
          upi_id: response.data.upi_id || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch bank details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API_URL}/admin-bank-details`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('Payment details saved successfully');
      fetchBankDetails();
    } catch (error) {
      toast.error('Failed to save payment details');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const CopyableField = ({ label, value, fieldKey }) => (
    <div 
      className="group cursor-pointer hover:bg-slate-50 rounded-lg p-3 -m-3 transition-colors"
      onClick={() => value && copyToClipboard(value, label)}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
          <p className="text-sm text-slate-900 font-medium mt-1">{value || '-'}</p>
        </div>
        {value && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            {copiedField === label ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4 text-slate-400" />
            )}
          </div>
        )}
      </div>
    </div>
  );

  // View-only mode for Agency/Master Agency
  if (!isAdmin) {
    return (
      <Sidebar>
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Official Payment Details
            </h1>
            <p className="text-slate-600 mt-2">Bank account details for payment processing</p>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading payment details...</div>
          ) : formData.account_holder_name ? (
            <div className="space-y-6">
              {/* Bank Account Details */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Bank Account Details</h2>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-800">
                    <strong>Click on any field to copy</strong> the value to your clipboard.
                  </p>
                </div>
                <div className="space-y-4">
                  <CopyableField label="Account Holder Name" value={formData.account_holder_name} fieldKey="account_holder_name" />
                  <CopyableField label="Account Number" value={formData.account_number} fieldKey="account_number" />
                  <CopyableField label="Bank Name" value={formData.bank_name} fieldKey="bank_name" />
                  <div className="grid grid-cols-2 gap-4">
                    <CopyableField label="IFSC Code" value={formData.ifsc_code} fieldKey="ifsc_code" />
                    <CopyableField label="Branch" value={formData.branch} fieldKey="branch" />
                  </div>
                </div>
              </div>

              {/* UPI Details */}
              {formData.upi_id && (
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <CreditCard className="w-5 h-5 text-emerald-600" />
                    <h2 className="text-lg font-semibold text-slate-900">UPI Payment</h2>
                  </div>
                  <CopyableField label="UPI ID" value={formData.upi_id} fieldKey="upi_id" />
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                Use these details for payment reconciliation and processing. Contact Admin for any discrepancies.
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
              <p className="text-slate-500">Payment details not available. Please contact Admin.</p>
            </div>
          )}
        </div>
      </Sidebar>
    );
  }

  // Edit mode for Admin/Sub Admin
  return (
    <Sidebar>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Official Payment Details
          </h1>
          <p className="text-slate-600 mt-2">Manage bank details visible to all agencies</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading payment details...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Bank Account Details Section */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-6">
                <Building2 className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-slate-900">Bank Account Details</h2>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-800 mb-6">
                <strong>Note:</strong> These payment details will be visible to all agencies and master agencies. Update carefully.
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="account_holder_name">Account Holder Name *</Label>
                  <Input
                    id="account_holder_name"
                    value={formData.account_holder_name}
                    onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
                    required
                    data-testid="account-holder-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="account_number">Account Number *</Label>
                  <Input
                    id="account_number"
                    value={formData.account_number}
                    onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                    required
                    data-testid="account-number-input"
                  />
                </div>

                <div>
                  <Label htmlFor="bank_name">Bank Name *</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    required
                    data-testid="bank-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ifsc_code">IFSC Code *</Label>
                    <Input
                      id="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({...formData, ifsc_code: e.target.value})}
                      required
                      data-testid="ifsc-code-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="branch">Branch *</Label>
                    <Input
                      id="branch"
                      value={formData.branch}
                      onChange={(e) => setFormData({...formData, branch: e.target.value})}
                      required
                      data-testid="branch-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* UPI Details Section */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-6">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                <h2 className="text-lg font-semibold text-slate-900">UPI Payment Details</h2>
              </div>

              <div>
                <Label htmlFor="upi_id">UPI ID</Label>
                <Input
                  id="upi_id"
                  value={formData.upi_id}
                  onChange={(e) => setFormData({...formData, upi_id: e.target.value})}
                  placeholder="example@upi or 9876543210@paytm"
                  data-testid="upi-id-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter your UPI ID for direct UPI payments (optional)
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-payment-details-button"
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {saving ? 'Saving...' : 'Save Payment Details'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Sidebar>
  );
}
